/**
 * Burndown.js
 * ===========
 * Licence burn-down league table — shows every active customer's
 * current contract year consumption. Each row has a "History" button
 * that expands all previous years inline with burn bars.
 *
 * Wire up:
 *   1. src/pages/Burndown.js
 *   2. App.js: { id: 'burndown', label: 'Licence burn-down' }
 *   3. api.js:
 *        export const getBurndown        = () => api.get('/api/v1/burndown');
 *        export const getBurndownHistory = (code) => api.get(`/api/v1/burndown/${code}/history`);
 */

import React, { useEffect, useState, useMemo } from 'react';
import { getBurndown, getBurndownHistory } from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtNum = n => n == null ? '—' : Number(n).toLocaleString();
const fmtPct = p => p == null ? null : `${p.toFixed(1)}%`;
const fmtGb  = g => g == null ? '—' : `${Number(g).toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`;

function severity(pct) {
  if (pct == null) return 'none';
  if (pct >= 90)   return 'critical';
  if (pct >= 75)   return 'warning';
  return 'ok';
}

const SEV_BAR   = { ok: '#1D9E75', warning: '#f59e0b', critical: '#ef4444' };
const SEV_BADGE = {
  ok:       { background: '#dcfce7', color: '#166534' },
  warning:  { background: '#fef3c7', color: '#92400e' },
  critical: { background: '#fee2e2', color: '#991b1b' },
  none:     { background: '#f3f4f6', color: '#6b7280' },
};

function BurnBar({ pct, periodPct, small = false }) {
  if (pct == null) {
    return <span style={{ fontSize: 11, color: '#bbb' }}>not configured</span>;
  }
  const capped   = Math.min(pct, 100);
  const sev      = severity(pct);
  const barColor = SEV_BAR[sev];
  const overrun  = pct > 100;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{
        position: 'relative', height: small ? 6 : 8, borderRadius: 4,
        background: '#e5e7eb', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${capped}%`,
          background: overrun
            ? 'repeating-linear-gradient(45deg,#ef4444,#ef4444 4px,#fca5a5 4px,#fca5a5 8px)'
            : barColor,
          borderRadius: 4,
          transition: 'width 0.4s ease',
        }} />
        {periodPct != null && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${Math.min(periodPct, 100)}%`,
            width: 2, background: 'rgba(0,0,0,0.25)',
          }} />
        )}
      </div>
      <div style={{ fontSize: 10, color: '#666', display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: barColor, fontWeight: 500 }}>{fmtPct(pct)}</span>
        {overrun && <span style={{ color: '#ef4444', fontWeight: 600 }}>OVER LIMIT</span>}
      </div>
    </div>
  );
}

function SevBadge({ pct }) {
  const sev   = severity(pct);
  const style = SEV_BADGE[sev];
  if (sev === 'none') return <span style={{ ...style, ...badgeBase }}>–</span>;
  return (
    <span style={{ ...style, ...badgeBase }}>
      {sev === 'critical' ? '⚠ Critical' : sev === 'warning' ? '⚡ Warning' : '✓ OK'}
    </span>
  );
}
const badgeBase = {
  display: 'inline-block', padding: '2px 8px',
  borderRadius: 20, fontSize: 11, fontWeight: 500,
};

function YearBar({ pct, isHistorical }) {
  if (pct == null) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`, height: '100%',
          background: isHistorical ? '#aaa' : '#378ADD', borderRadius: 2,
        }} />
      </div>
      <span style={{ fontSize: 10, color: '#aaa', whiteSpace: 'nowrap' }}>
        {isHistorical ? 'completed' : `${pct.toFixed(0)}% elapsed`}
      </span>
    </div>
  );
}

// ─── Per-customer history panel ───────────────────────────────────────────────

function HistoryPanel({ custCode, currentYear }) {
  const [years,   setYears]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    getBurndownHistory(custCode)
      .then(r => { setYears(r.data.years); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [custCode]);

  if (loading) return (
    <tr>
      <td colSpan={6} style={{ padding: '12px 24px', background: '#f9fafb', fontSize: 12, color: '#aaa' }}>
        Loading history…
      </td>
    </tr>
  );

  if (error) return (
    <tr>
      <td colSpan={6} style={{ padding: '12px 24px', background: '#fef2f2', fontSize: 12, color: '#991b1b' }}>
        Error: {error}
      </td>
    </tr>
  );

  const pastYears = (years || []).filter(y => y.year_number < currentYear).reverse();

  if (pastYears.length === 0) return (
    <tr>
      <td colSpan={6} style={{ padding: '12px 24px', background: '#f9fafb', fontSize: 12, color: '#bbb' }}>
        No previous contract years found.
      </td>
    </tr>
  );

  return (
    <>
      {pastYears.map(y => (
        <tr key={y.year_number} style={{ background: '#f9fafb', borderBottom: '1px solid #f0f0ee' }}>

          {/* Indent marker for history rows */}
          <td style={{ padding: '10px 14px 10px 32px' }}>
            <div style={{ fontSize: 10, color: '#bbb' }}>◀ historical</div>
          </td>

          {/* Dates + bundle name */}
          <td style={{ padding: '10px 14px', fontSize: 11, color: '#777' }}>
            <div>{fmtDate(y.period_start)}</div>
            <div style={{ color: '#bbb' }}>→ {fmtDate(y.period_end)}</div>
            {y.bundle_name && (
              <div style={{
                marginTop: 4, display: 'inline-block',
                fontSize: 10, padding: '1px 6px', borderRadius: 4,
                background: '#f0f7ff', color: '#378ADD', fontWeight: 500,
              }}>{y.bundle_name}</div>
            )}
            <div style={{ marginTop: 4 }}>
              <YearBar pct={100} isHistorical={true} />
            </div>
          </td>

          {/* Samples */}
          <td style={{ padding: '10px 14px' }}>
            <BurnBar pct={y.sample_pct} periodPct={null} small />
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              {fmtNum(y.used_samples)}
              {y.sample_limit ? ` / ${fmtNum(y.sample_limit)}` : ''}
            </div>
          </td>

          {/* Tests */}
          <td style={{ padding: '10px 14px' }}>
            <BurnBar pct={y.test_pct} periodPct={null} small />
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              {fmtNum(y.used_tests)}
              {y.test_limit ? ` / ${fmtNum(y.test_limit)}` : ''}
            </div>
          </td>

          {/* Storage — not year-specific */}
          <td style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, color: '#ccc', fontStyle: 'italic' }}>n/a for past years</div>
          </td>

          {/* Status */}
          <td style={{ padding: '10px 14px' }}>
            <SevBadge pct={
              [y.sample_pct, y.test_pct]
                .filter(p => p != null)
                .sort((a, b) => b - a)[0] ?? null
            } />
          </td>
        </tr>
      ))}
    </>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: 'sample_pct_desc',  label: 'Samples % (high first)' },
  { value: 'test_pct_desc',    label: 'Tests % (high first)' },
  { value: 'storage_pct_desc', label: 'Storage % (high first)' },
  { value: 'cust_name_asc',    label: 'Customer A→Z' },
];

function sortCustomers(customers, sortKey) {
  const sorted = [...customers];
  switch (sortKey) {
    case 'sample_pct_desc':
      return sorted.sort((a, b) => (b.sample_pct ?? -1) - (a.sample_pct ?? -1));
    case 'test_pct_desc':
      return sorted.sort((a, b) => (b.test_pct ?? -1) - (a.test_pct ?? -1));
    case 'storage_pct_desc':
      return sorted.sort((a, b) => (b.storage_pct ?? -1) - (a.storage_pct ?? -1));
    default:
      return sorted.sort((a, b) => a.cust_name.localeCompare(b.cust_name));
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Burndown() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [sort,     setSort]     = useState('sample_pct_desc');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null); // cust_code with open history

  useEffect(() => {
    setLoading(true);
    getBurndown()
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const customers = useMemo(() => {
    if (!data?.customers) return [];
    const filtered = search
      ? data.customers.filter(c =>
          c.cust_name.toLowerCase().includes(search.toLowerCase()) ||
          c.cust_code.toLowerCase().includes(search.toLowerCase())
        )
      : data.customers;
    return sortCustomers(filtered, sort);
  }, [data, sort, search]);

  if (loading) return <div className="loading">Loading burn-down…</div>;
  if (error)   return <div className="error">Error: {error}</div>;

  const { at_risk, warning, configured, total, as_of } = data;
  const unconfigured = total - configured;

  return (
    <>
      {/* ── Summary metrics ─────────────────────────────────── */}
      <div className="metrics" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))', marginBottom: 20 }}>
        <div className="metric">
          <div className="metric-label">Critical (≥ 90%)</div>
          <div className="metric-value" style={{ color: at_risk > 0 ? '#ef4444' : '#1a1a1a' }}>{at_risk}</div>
          <div className="metric-sub">customers at or near limit</div>
        </div>
        <div className="metric">
          <div className="metric-label">Warning (75–89%)</div>
          <div className="metric-value" style={{ color: warning > 0 ? '#f59e0b' : '#1a1a1a' }}>{warning}</div>
          <div className="metric-sub">approaching limit</div>
        </div>
        <div className="metric">
          <div className="metric-label">Configured</div>
          <div className="metric-value">{configured} / {total}</div>
          <div className="metric-sub">
            {unconfigured > 0 ? `${unconfigured} without limits set` : 'all customers configured'}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">As of</div>
          <div className="metric-value" style={{ fontSize: 15 }}>
            {new Date(as_of).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
          <div className="metric-sub">current contract year</div>
        </div>
      </div>

      {/* ── Controls ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          className="search-input"
          style={{ marginBottom: 0, maxWidth: 300 }}
          placeholder="Search customer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={{ fontSize: 13, padding: '7px 10px', border: '1px solid #e5e5e5', borderRadius: 6 }}
          value={sort}
          onChange={e => setSort(e.target.value)}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: '#999', marginLeft: 4 }}>
          {customers.length} customer{customers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Legend ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 12, fontSize: 11, color: '#666' }}>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: SEV_BAR.ok, marginRight: 5 }} />
          On track (&lt; 75%)
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: SEV_BAR.warning, marginRight: 5 }} />
          Warning (75–89%)
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: SEV_BAR.critical, marginRight: 5 }} />
          Critical (≥ 90%)
        </span>
        <span style={{ color: '#aaa' }}>┊ vertical line = % of licence year elapsed</span>
      </div>

      {/* ── League table ────────────────────────────────────── */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9f9f8' }}>
              <th style={TH}>Customer</th>
              <th style={TH}>Usage year</th>
              <th style={{ ...TH, width: 200 }}>Samples</th>
              <th style={{ ...TH, width: 200 }}>Tests</th>
              <th style={{ ...TH, width: 200 }}>Storage (GB)</th>
              <th style={TH}>Status</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(c => {
              const isOpen     = expanded === c.cust_code;
              const hasHistory = c.current_year > 1;
              return (
                <React.Fragment key={c.cust_code}>
                  <tr
                    style={{ borderBottom: '1px solid #f0f0ee' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafaf9'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    {/* Customer */}
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 500 }}>{c.cust_name}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{c.cust_code} · {c.location || '—'}</div>

                      {hasHistory && (
                        <button
                          onClick={() => setExpanded(isOpen ? null : c.cust_code)}
                          style={{
                            marginTop: 5, fontSize: 10, padding: '2px 8px',
                            borderRadius: 4, border: '1px solid #e5e5e5',
                            background: isOpen ? '#f3f4f6' : '#fff',
                            cursor: 'pointer', color: '#555',
                          }}
                        >
                          {isOpen ? '▲ Hide history' : '▼ History'}
                        </button>
                      )}
                    </td>

                    {/* Licence year */}
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>
                      {c.period_start ? (
                        <>
                          <div>{fmtDate(c.period_start)}</div>
                          <div style={{ color: '#bbb' }}>→ {fmtDate(c.period_end)}</div>
                          <div style={{ marginTop: 4 }}>
                            <YearBar pct={c.period_pct} isHistorical={false} />
                          </div>
                        </>
                      ) : (
                        <span style={{ color: '#ccc', fontSize: 11 }}>not configured</span>
                      )}
                    </td>

                    {/* Samples */}
                    <td style={{ padding: '12px 14px' }}>
                      <BurnBar pct={c.sample_pct} periodPct={c.period_pct} />
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {fmtNum(c.used_samples)}
                        {c.sample_limit ? ` / ${fmtNum(c.sample_limit)}` : ''}
                        {c.sample_remaining != null && c.sample_remaining >= 0
                          ? ` · ${fmtNum(c.sample_remaining)} left` : ''}
                      </div>
                    </td>

                    {/* Tests */}
                    <td style={{ padding: '12px 14px' }}>
                      <BurnBar pct={c.test_pct} periodPct={c.period_pct} />
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {fmtNum(c.used_tests)}
                        {c.test_limit ? ` / ${fmtNum(c.test_limit)}` : ''}
                        {c.test_remaining != null && c.test_remaining >= 0
                          ? ` · ${fmtNum(c.test_remaining)} left` : ''}
                      </div>
                    </td>

                    {/* Storage */}
                    <td style={{ padding: '12px 14px' }}>
                      <BurnBar pct={c.storage_pct} periodPct={null} />
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {fmtGb(c.total_storage_gb)}
                        {c.storage_limit_gb ? ` / ${fmtGb(c.storage_limit_gb)}` : ''}
                        {c.storage_remaining_gb != null && c.storage_remaining_gb >= 0
                          ? ` · ${fmtGb(c.storage_remaining_gb)} left` : ''}
                      </div>
                      {(c.volumes_gb > 0 || c.db_gb > 0) && (
                        <div style={{ fontSize: 10, color: '#bbb', marginTop: 3 }}>
                          <span title="Allocated E: drive volumes">Vol: {fmtGb(c.volumes_gb)}</span>
                          {' · '}
                          <span title="SQL Server database size">DB: {fmtGb(c.db_gb)}</span>
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '12px 14px' }}>
                      <SevBadge pct={
                        [c.sample_pct, c.test_pct, c.storage_pct]
                          .filter(p => p != null)
                          .sort((a, b) => b - a)[0] ?? null
                      } />
                    </td>
                  </tr>

                  {/* Expanded history rows */}
                  {isOpen && (
                    <HistoryPanel custCode={c.cust_code} currentYear={c.current_year} />
                  )}
                </React.Fragment>
              );
            })}

            {customers.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
                  No customers match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: '#bbb', marginTop: 8 }}>
        Limits are set per customer in the Customers page. Storage reflects the latest volume snapshot.
      </div>
    </>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const TH = {
  padding: '10px 14px', textAlign: 'left',
  fontWeight: 500, fontSize: 12, color: '#666',
  borderBottom: '1px solid #e5e5e5',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
